import React, { ComponentProps, useEffect, useState } from 'react';

import App from '@/app/App';
import EmbeddedShell from '@/app/layout-shells/embedded-shell/EmbeddedShell';
import { QueryParams } from '@/shared/types/tile-packet.types';

type Props = ComponentProps<typeof App> & {
  query: QueryParams;
};

export default function EmbeddedApp(props: Props) {
  // Bootstrapping: when ?waitForData=…&iframeId=… is set the host
  // page hasn't sent us the embedded TilePacket yet, so we paint the
  // loading shell and wait for a postMessage with the data.
  const [query, setQuery] = useState<QueryParams>(props.query);
  const [receivedDataEvent, setReceivedDataEvent] = useState(false);

  useEffect(() => {
    if (!query || !query.waitForData || !query.iframeId) return;
    const iframeId = query.iframeId;
    window.parent.postMessage(['expoFrameLoaded', { iframeId }], '*');

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!Array.isArray(data) || data[0] !== 'expoDataEvent') return;
      const payload = data[1];
      if (payload?.iframeId !== iframeId) return;
      setQuery((q) => ({ ...q, ...payload }));
      setReceivedDataEvent(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // Effect runs once on mount — query.waitForData / query.iframeId
    // don't change after first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (query && query.waitForData && !receivedDataEvent) {
    return <EmbeddedShell />;
  }

  return <App {...props} query={query} isEmbedded />;
}
